import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.js'
import { asDownload } from '../lib/files.js'
import { Icon } from './Icon.js'
import { useOverlay } from './overlay.js'
import { buttonClass } from './ui.js'

/** What the viewer needs to draw a file. `FeedFile` from the job card is one. */
export interface ViewerFile {
  id: string
  name: string
  /** Three or four letters off the filename: JPG, PDF, HEIC. */
  kind: string
  isImage: boolean
  meta: string
  /** Null until the bytes exist. Such a file is skipped, not drawn broken. */
  url: string | null
}

/**
 * A file opened where you are, before any new tab.
 *
 * A photo fills the screen and the arrows, the arrow keys, a swipe or the strip
 * along the bottom step through the rest. A PDF is drawn by the browser's own
 * viewer in a frame. Anything else says there is no preview. Every file keeps
 * **Open in new tab** and **Download**, because the viewer is the first step
 * and not the only one.
 *
 * Its own overlay rather than `Modal`: the photo wants the whole screen on a
 * dark field, not a white card. It stacks through `useOverlay`, so Escape
 * closes the viewer and leaves the job card underneath open.
 */
export function FileViewer({
  files,
  openId,
  onClose,
}: {
  files: readonly ViewerFile[]
  /** The file to show, or null for a closed viewer. */
  openId: string | null
  onClose: () => void
}) {
  const viewable = files.filter((file) => file.url)
  // Held by id rather than position, so a refetch that adds a photo does not
  // swap the one on screen for its neighbour.
  const [currentId, setCurrentId] = useState(openId)
  useEffect(() => setCurrentId(openId), [openId])

  const found = viewable.findIndex((file) => file.id === currentId)
  const index = found === -1 ? viewable.findIndex((file) => file.id === openId) : found
  const open = openId !== null && index !== -1
  const depth = useOverlay(open, onClose)
  const panel = useRef<HTMLDivElement>(null)
  const swipe = useRef<number | null>(null)

  useEffect(() => {
    if (open) panel.current?.focus()
  }, [open])

  const count = viewable.length
  const file = open ? viewable[index] : undefined
  const step = (by: number) => setCurrentId(viewable[(index + by + count) % count]!.id)

  // The neighbours, fetched while this one is looked at, so the next click
  // is not a wait on a photo coming through the API.
  const before = count > 1 ? viewable[(index - 1 + count) % count]?.url : null
  const after = count > 1 ? viewable[(index + 1) % count]?.url : null
  useEffect(() => {
    for (const url of [after, before]) if (url) new Image().src = url
  }, [after, before])

  if (!open || !file?.url) return null

  const many = count > 1
  const photos = viewable.filter((entry) => entry.isImage)

  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-label={file.name}
      tabIndex={-1}
      className="fixed inset-0 flex flex-col bg-ink-900 text-white outline-none"
      style={{ zIndex: 50 + depth * 10 }}
      onKeyDown={(event) => {
        if (!many || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return
        // The job card and the screens under it are this element's React
        // parents, and the arrows are the viewer's while it is open.
        event.stopPropagation()
        step(event.key === 'ArrowRight' ? 1 : -1)
      }}
    >
      <header className="flex shrink-0 items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{file.name}</p>
          <p className="truncate text-xs text-ink-300">
            {many ? `${index + 1} of ${count} · ` : ''}
            {file.meta}
          </p>
        </div>
        <a
          href={file.url}
          target="_blank"
          rel="noreferrer"
          className={buttonClass({ size: 'sm', variant: 'ghost', className: 'text-ink-100 hover:bg-ink-800 hover:text-white' })}
        >
          <Icon name="external" className="size-4" />
          <span className="hidden sm:inline">Open in new tab</span>
        </a>
        <a
          href={asDownload(file.url)}
          aria-label="Download"
          className={buttonClass({ size: 'sm', variant: 'ghost', className: 'text-ink-100 hover:bg-ink-800 hover:text-white' })}
        >
          <Icon name="download" className="size-4" />
          <span className="hidden sm:inline">Download</span>
        </a>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="grid size-9 shrink-0 place-items-center rounded-md text-ink-100 hover:bg-ink-800 hover:text-white"
        >
          <Icon name="close" />
        </button>
      </header>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-2 sm:px-16"
        onPointerDown={(event) => {
          swipe.current = event.clientX
        }}
        onPointerUp={(event) => {
          const from = swipe.current
          swipe.current = null
          if (from === null || !many || event.pointerType === 'mouse') return
          const moved = event.clientX - from
          if (Math.abs(moved) > 50) step(moved < 0 ? 1 : -1)
        }}
      >
        {file.isImage ? (
          <img
            key={file.id}
            src={file.url}
            alt={file.name}
            className="max-h-full max-w-full select-none rounded-md object-contain"
            draggable={false}
          />
        ) : file.kind === 'PDF' ? (
          <iframe
            key={file.id}
            src={file.url}
            title={file.name}
            className="size-full rounded-md bg-white"
          />
        ) : (
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-ink-200">There is no preview for a {file.kind} file.</p>
            <a href={asDownload(file.url)} className={buttonClass({ variant: 'primary' })}>
              Download it
            </a>
          </div>
        )}

        {many ? (
          <>
            <NavButton side="left" label="Previous" onClick={() => step(-1)} />
            <NavButton side="right" label="Next" onClick={() => step(1)} />
          </>
        ) : null}
      </div>

      {file.isImage && photos.length > 1 ? (
        <div className="flex shrink-0 justify-center gap-1.5 overflow-x-auto px-4 pb-4">
          {photos.map((photo) => (
            <button
              key={photo.id}
              type="button"
              aria-label={`Show ${photo.name}`}
              aria-current={photo.id === file.id ? 'true' : undefined}
              onClick={() => setCurrentId(photo.id)}
              className={cn(
                'size-12 shrink-0 overflow-hidden rounded-md ring-2 transition-opacity',
                photo.id === file.id ? 'ring-white' : 'opacity-50 ring-transparent hover:opacity-100',
              )}
            >
              <img src={photo.url!} alt="" loading="lazy" className="size-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}
    </div>,
    document.body,
  )
}

function NavButton({ side, label, onClick }: { side: 'left' | 'right'; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-ink-800 text-white ring-1 ring-ink-700 hover:bg-ink-700',
        side === 'left' ? 'left-2 sm:left-4' : 'right-2 sm:right-4',
      )}
    >
      <Icon name={side === 'left' ? 'back' : 'forward'} />
    </button>
  )
}
